// Inventory operations that move or correct stock:
//   Stock Transfer — warehouse ↔ warehouse / bin ↔ bin (draft, then post)
//   Cycle Count    — generate a count sheet, key the physical counts, post the variances
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X, ArrowLeftRight, ClipboardList } from 'lucide-react';
import { api } from '@/api/client';
import { ExportImportBar } from '@/components/ui/ExportImportBar';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import {
  Badge, F, WmsCard, WmsPageBar, WmsSearch,
  productOptions, useAllProducts, useActiveWarehouses, useSearchPaged, useWmsMerchants,
  useWmsViewOnly,
} from './WmsShared';

// Why stock is being written on or off. A fixed list (matching the backend) so the adjustment
// report can be grouped by reason — the specifics go in the remarks.
export const ADJUSTMENT_REASONS = [
  { value: 'DAMAGE', label: 'Damage' },
  { value: 'THEFT_SHRINKAGE', label: 'Theft / Shrinkage' },
  { value: 'MISCOUNT', label: 'Miscount' },
  { value: 'SYSTEM_ERROR', label: 'System error' },
  { value: 'FOUND_STOCK', label: 'Found stock' },
  { value: 'EXPIRY', label: 'Expiry' },
  { value: 'OTHER', label: 'Other' },
] as const;

// ──────────────────────────── STOCK TRANSFER ────────────────────────────

export function StockTransferTab() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [show, setShow] = useState(false);
  const [active, setActive] = useState<any>(null);
  const { data = [] } = useQuery({ queryKey: ['wms-transfers'], queryFn: async () => (await api.get('/wms/stock-transfers')).data as any[] });
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();
  const mName = (id: any) => (merchants as any[]).find((m) => m.id === id)?.companyName ?? (id ? `M#${id}` : '—');
  const wName = (id: any) => (warehouses as any[]).find((w) => w.id === id)?.name ?? (id ? `W#${id}` : '—');

  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    data as any[],
    (t: any, term) => [t.transferNo, t.status, t.vehicleNo, t.remarks, mName(t.merchantId), wName(t.fromWarehouseId), wName(t.toWarehouseId)]
      .some((v) => String(v ?? '').toLowerCase().includes(term)),
  );
  const refresh = () => { qc.invalidateQueries({ queryKey: ['wms-transfers'] }); qc.invalidateQueries({ queryKey: ['wms-stock'] }); };

  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><ArrowLeftRight size={16} /> Stock Transfer</span>}
      actions={
        <>
          <WmsSearch value={q} onChange={setQ} placeholder="Search transfer / warehouse / status" className="w-72" />
          {!viewOnly && <ExportImportBar resource="wms/stock-transfers" onImported={refresh} />}
          {!viewOnly && <button onClick={() => setShow(true)} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> New Transfer</button>}
        </>
      }
    >
      <p className="text-xs text-slate-500 mb-3">A transfer is a draft until you complete it — stock only moves at completion, and a completed transfer is reversed with a new transfer, never edited.</p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">Transfer No</th>{!viewOnly && <th className="text-left">Merchant</th>}
            <th className="text-left">From</th><th className="text-left">To</th><th className="text-right">Qty</th>
            <th className="text-left">Lines</th><th>Status</th><th>Date</th><th />
          </tr></thead>
          <tbody>
            {total === 0
              ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">{q ? 'No matches' : 'No stock transfers yet'}</td></tr>
              : pageRows.map((t: any) => (
                <tr key={t.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => setActive(t)}>
                  <td className="p-2 font-mono text-xs">{t.transferNo}</td>
                  {!viewOnly && <td className="text-xs">{mName(t.merchantId)}</td>}
                  <td className="text-xs">{wName(t.fromWarehouseId)}</td>
                  <td className="text-xs">{wName(t.toWarehouseId)}</td>
                  <td className="text-right">{t.totalQty}</td>
                  <td className="text-xs">{(t.items ?? []).length}</td>
                  <td><Badge text={t.status} /></td>
                  <td className="text-xs text-slate-500">{(t.transferDate ?? t.createdAt)?.slice(0, 10)}</td>
                  <td className="text-xs text-emerald-700">Open →</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {show && <TransferForm onClose={() => setShow(false)} onSaved={() => { setShow(false); refresh(); }} />}
      {active && <TransferModal transfer={active} viewOnly={viewOnly} wName={wName} onClose={() => setActive(null)} onSaved={() => { refresh(); setActive(null); }} />}
    </WmsCard>
  );
}

function TransferForm({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ items: [{ productId: '', qty: 1 }] });
  const products = useAllProducts();
  const options = productOptions(products);
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();

  const setItem = (i: number, k: string, v: any) => setForm((f: any) => ({ ...f, items: f.items.map((x: any, idx: number) => idx === i ? { ...x, [k]: v } : x) }));
  const addRow = () => setForm((f: any) => ({ ...f, items: [...f.items, { productId: '', qty: 1 }] }));
  const rmRow = (i: number) => setForm((f: any) => ({ ...f, items: f.items.filter((_: any, idx: number) => idx !== i) }));

  const save = useMutation({
    mutationFn: async () => (await api.post('/wms/stock-transfers', form)).data,
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not create the transfer'),
  });
  const sameWh = form.fromWarehouseId && form.fromWarehouseId === form.toWarehouseId;
  const canSave = !!form.merchantId && !!form.fromWarehouseId && !!form.toWarehouseId
    && form.items.length > 0 && form.items.every((i: any) => i.productId && Number(i.qty) > 0)
    && (!sameWh || form.items.every((i: any) => String(i.fromBin ?? '').trim() !== String(i.toBin ?? '').trim()));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[920px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">New Stock Transfer</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <F label="Merchant *"><select value={form.merchantId ?? ''} onChange={(e) => setForm({ ...form, merchantId: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(merchants as any[]).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
            <F label="From Warehouse *"><select value={form.fromWarehouseId ?? ''} onChange={(e) => setForm({ ...form, fromWarehouseId: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
            <F label="To Warehouse *"><select value={form.toWarehouseId ?? ''} onChange={(e) => setForm({ ...form, toWarehouseId: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
            <F label="Transfer Date"><input type="date" value={form.transferDate ?? ''} onChange={(e) => setForm({ ...form, transferDate: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Vehicle No"><input value={form.vehicleNo ?? ''} onChange={(e) => setForm({ ...form, vehicleNo: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Remarks"><input value={form.remarks ?? ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          </div>
          {sameWh && <div className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">Same warehouse on both sides — this is a bin-to-bin move, so each line needs a different From Bin and To Bin.</div>}
          <div>
            <div className="text-sm font-medium mb-2">Items</div>
            {form.items.map((it: any, i: number) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                <div className="col-span-4"><F label="Product *"><SearchableSelect value={it.productId || ''} onChange={(v) => setItem(i, 'productId', v)} options={options} placeholder="Search SKU / name…" /></F></div>
                <div className="col-span-2"><F label="Qty *"><input type="number" min={1} value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
                <div className="col-span-2"><F label="From Bin"><input value={it.fromBin ?? ''} onChange={(e) => setItem(i, 'fromBin', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F></div>
                <div className="col-span-2"><F label="To Bin"><input value={it.toBin ?? ''} onChange={(e) => setItem(i, 'toBin', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F></div>
                <div className="col-span-1"><F label="Batch"><input value={it.batchNo ?? ''} onChange={(e) => setItem(i, 'batchNo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F></div>
                <div className="col-span-1">{form.items.length > 1 && <button onClick={() => rmRow(i)} className="border border-red-300 text-red-600 p-1.5 rounded"><Trash2 size={14} /></button>}</div>
              </div>
            ))}
            <button onClick={addRow} className="text-emerald-700 border border-emerald-300 px-3 py-1 rounded text-xs flex items-center gap-1"><Plus size={12} /> Add line</button>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <div className="text-xs text-slate-500 mr-auto">Saved as a draft — stock moves when you complete it.</div>
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !canSave} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Create Transfer'}</button>
        </div>
      </div>
    </div>
  );
}

function TransferModal({ transfer, viewOnly, wName, onClose, onSaved }: any) {
  const { data: fresh } = useQuery({ queryKey: ['wms-transfer', transfer.id], queryFn: async () => (await api.get(`/wms/stock-transfers/${transfer.id}`)).data });
  const t = fresh ?? transfer;
  const complete = useMutation({
    mutationFn: async () => (await api.post(`/wms/stock-transfers/${t.id}/complete`)).data,
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not complete the transfer'),
  });
  const cancel = useMutation({
    mutationFn: async () => (await api.patch(`/wms/stock-transfers/${t.id}/cancel`)).data,
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not cancel'),
  });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[900px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2">{t.transferNo} <Badge text={t.status} /></div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-4 gap-3 text-sm">
            <F label="From"><div className="text-slate-700">{wName(t.fromWarehouseId)}</div></F>
            <F label="To"><div className="text-slate-700">{wName(t.toWarehouseId)}</div></F>
            <F label="Transfer Date"><div className="text-slate-700">{(t.transferDate ?? t.createdAt)?.slice(0, 10)}</div></F>
            <F label="Vehicle"><div className="text-slate-700">{t.vehicleNo ?? '—'}</div></F>
          </div>
          {t.status === 'DRAFT' && <div className="text-xs bg-amber-50 text-amber-800 rounded px-3 py-2">Completing moves this stock out of the source location and into the destination. It cannot be undone — correct a mistake with a reverse transfer.</div>}
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-xs"><tr>
              <th className="p-2 text-left">Product</th><th className="text-right">Qty</th><th className="text-left">From Bin</th><th className="text-left">To Bin</th><th className="text-left">Batch</th>
            </tr></thead>
            <tbody>
              {(t.items ?? []).map((i: any) => (
                <tr key={i.id} className="border-t">
                  <td className="p-2">{i.product?.sku} — {i.product?.name}</td>
                  <td className="text-right">{i.qty}</td>
                  <td className="font-mono text-xs">{i.fromBin || '—'}</td>
                  <td className="font-mono text-xs">{i.toBin || '—'}</td>
                  <td className="font-mono text-xs">{i.batchNo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border px-4 py-1.5 rounded text-sm">Close</button>
          {!viewOnly && t.status === 'DRAFT' && <button onClick={() => cancel.mutate()} disabled={cancel.isPending} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm disabled:opacity-50">Cancel Transfer</button>}
          {!viewOnly && t.status === 'DRAFT' && <button onClick={() => complete.mutate()} disabled={complete.isPending} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{complete.isPending ? 'Moving…' : 'Complete → Move Stock'}</button>}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────── CYCLE COUNT ─────────────────────────────

export function CycleCountTab() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [show, setShow] = useState(false);
  const [active, setActive] = useState<any>(null);
  const { data = [] } = useQuery({ queryKey: ['wms-cycle-counts'], queryFn: async () => (await api.get('/wms/cycle-counts')).data as any[] });
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();
  const mName = (id: any) => (merchants as any[]).find((m) => m.id === id)?.companyName ?? (id ? `M#${id}` : '—');
  const wName = (id: any) => (warehouses as any[]).find((w) => w.id === id)?.name ?? (id ? `W#${id}` : '—');

  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    data as any[],
    (c: any, t) => [c.countNo, c.status, c.countedByName, mName(c.merchantId), wName(c.warehouseId)].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const refresh = () => { qc.invalidateQueries({ queryKey: ['wms-cycle-counts'] }); qc.invalidateQueries({ queryKey: ['wms-stock'] }); };

  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><ClipboardList size={16} /> Cycle Count</span>}
      actions={
        <>
          <WmsSearch value={q} onChange={setQ} placeholder="Search count / warehouse / status" className="w-72" />
          {!viewOnly && <ExportImportBar resource="wms/cycle-counts" onImported={refresh} />}
          {!viewOnly && <button onClick={() => setShow(true)} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> New Count Sheet</button>}
        </>
      }
    >
      <p className="text-xs text-slate-500 mb-3">Generate a sheet from current stock, key in what you physically counted, then post — every variance is written as a stock adjustment.</p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">Count No</th>{!viewOnly && <th className="text-left">Merchant</th>}
            <th className="text-left">Warehouse</th><th className="text-right">Lines</th><th className="text-right">Variance</th>
            <th className="text-left">Counted By</th><th>Status</th><th>Date</th><th />
          </tr></thead>
          <tbody>
            {total === 0
              ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">{q ? 'No matches' : 'No cycle counts yet'}</td></tr>
              : pageRows.map((c: any) => (
                <tr key={c.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => setActive(c)}>
                  <td className="p-2 font-mono text-xs">{c.countNo}</td>
                  {!viewOnly && <td className="text-xs">{mName(c.merchantId)}</td>}
                  <td className="text-xs">{wName(c.warehouseId)}</td>
                  <td className="text-right">{(c.items ?? []).length}</td>
                  <td className={`text-right ${Number(c.totalVariance) < 0 ? 'text-red-600' : Number(c.totalVariance) > 0 ? 'text-emerald-700' : ''}`}>
                    {Number(c.totalVariance) > 0 ? '+' : ''}{c.totalVariance}
                  </td>
                  <td className="text-xs">{c.countedByName ?? '—'}</td>
                  <td><Badge text={c.status} /></td>
                  <td className="text-xs text-slate-500">{(c.countDate ?? c.createdAt)?.slice(0, 10)}</td>
                  <td className="text-xs text-emerald-700">Open →</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {show && <CycleCountForm onClose={() => setShow(false)} onSaved={(c: any) => { setShow(false); refresh(); setActive(c); }} />}
      {active && <CycleCountModal count={active} viewOnly={viewOnly} onClose={() => setActive(null)} onSaved={() => { refresh(); }} />}
    </WmsCard>
  );
}

function CycleCountForm({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();
  const save = useMutation({
    mutationFn: async () => (await api.post('/wms/cycle-counts', form)).data,
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not build the count sheet'),
  });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-24 z-50">
      <div className="bg-white rounded-lg w-[620px]">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">New Cycle Count Sheet</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <F label="Merchant *"><select value={form.merchantId ?? ''} onChange={(e) => setForm({ ...form, merchantId: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(merchants as any[]).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
          <F label="Warehouse *"><select value={form.warehouseId ?? ''} onChange={(e) => setForm({ ...form, warehouseId: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
          <F label="Bin (optional)"><input value={form.bin ?? ''} onChange={(e) => setForm({ ...form, bin: e.target.value })} placeholder="count just one bin" className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
          <F label="Count Date"><input type="date" value={form.countDate ?? ''} onChange={(e) => setForm({ ...form, countDate: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Counted By"><input value={form.countedByName ?? ''} onChange={(e) => setForm({ ...form, countedByName: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <F label="Remarks"><input value={form.remarks ?? ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          <p className="col-span-2 text-xs text-slate-500">The sheet snapshots every SKU with stock on hand at that location. DAMAGE bins are left out — they aren't sellable stock.</p>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !form.merchantId || !form.warehouseId} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Building…' : 'Generate Sheet'}</button>
        </div>
      </div>
    </div>
  );
}

function CycleCountModal({ count, viewOnly, onClose, onSaved }: any) {
  const { data: fresh } = useQuery({ queryKey: ['wms-cycle-count', count.id], queryFn: async () => (await api.get(`/wms/cycle-counts/${count.id}`)).data });
  const c = fresh ?? count;
  const [items, setItems] = useState<any[]>(() => seed(count));
  const [seededFor, setSeededFor] = useState<any>(null);
  if (fresh && seededFor !== fresh) { setSeededFor(fresh); setItems(seed(fresh)); }

  function seed(x: any) {
    return (x.items ?? []).map((i: any) => ({
      id: i.id, sku: i.product?.sku, name: i.product?.name, bin: i.bin ?? '', batchNo: i.batchNo ?? '',
      systemQty: Number(i.systemQty ?? 0), countedQty: i.countedQty ?? '', remarks: i.remarks ?? '', posted: !!i.posted,
    }));
  }
  const setIt = (idx: number, k: string, v: any) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, [k]: v } : x));
  const editable = !viewOnly && c.status !== 'POSTED' && c.status !== 'CANCELLED';
  const allCounted = items.length > 0 && items.every((i) => i.countedQty !== '' && i.countedQty != null);

  // Header fields that justify the adjustment. They are edited here and saved with the counts,
  // because both have to be on file before the variance can be approved and posted.
  const [reason, setReason] = useState<string>(c.reason ?? '');
  const [remarks, setRemarks] = useState<string>(c.remarks ?? '');
  const [hdrSeededFor, setHdrSeededFor] = useState<any>(null);
  if (fresh && hdrSeededFor !== fresh) { setHdrSeededFor(fresh); setReason(fresh.reason ?? ''); setRemarks(fresh.remarks ?? ''); }

  const approved = !!c.approvedAt;
  const countPayload = () => ({
    items: items.map((i) => ({ id: i.id, countedQty: i.countedQty, remarks: i.remarks })),
    reason, remarks,
  });

  const qc = useQueryClient();
  const refetch = () => qc.invalidateQueries({ queryKey: ['wms-cycle-count', c.id] });
  const saveCounts = useMutation({
    mutationFn: async () => (await api.post(`/wms/cycle-counts/${c.id}/count`, countPayload())).data,
    onSuccess: () => { refetch(); onSaved(); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not save the counts'),
  });
  const approve = useMutation({
    mutationFn: async () => {
      // Save first, so the approver signs off the numbers actually on screen.
      await api.post(`/wms/cycle-counts/${c.id}/count`, countPayload());
      const who = window.prompt('Name of the person authorising this stock adjustment:');
      if (!who || !who.trim()) throw new Error('Approval cancelled — an authoriser name is required');
      return (await api.post(`/wms/cycle-counts/${c.id}/approve`, { approvedByName: who.trim() })).data;
    },
    onSuccess: () => { refetch(); onSaved(); },
    onError: (e: any) => alert(e?.response?.data?.message ?? e?.message ?? 'Could not approve the adjustment'),
  });
  const post = useMutation({
    // Save first — otherwise counts typed but not yet saved would post the previous (or empty) numbers.
    mutationFn: async () => {
      await api.post(`/wms/cycle-counts/${c.id}/count`, countPayload());
      return (await api.post(`/wms/cycle-counts/${c.id}/post`)).data;
    },
    onSuccess: (r: any) => { refetch(); onSaved(); alert(`Posted — ${r.adjusted} line(s) adjusted.`); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not post the count'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[960px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2">{c.countNo} <Badge text={c.status} /></div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {c.status !== 'POSTED' && <div className="text-xs bg-blue-50 text-blue-800 rounded px-3 py-2">Enter what you physically counted. A reason and remarks are required, and the adjustment must be authorised before the variances can be posted to stock.</div>}

          {/* Why the stock differs, and who signed it off. Both are on the record before anything moves. */}
          <div className="grid grid-cols-3 gap-3">
            <F label="Reason *">
              <select value={reason} disabled={!editable} onChange={(e) => setReason(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">— select —</option>
                {ADJUSTMENT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </F>
            <div className="col-span-2">
              <F label="Remarks *">
                <input value={remarks} disabled={!editable} onChange={(e) => setRemarks(e.target.value)}
                  placeholder="What was found, and why the stock differs" className="w-full border rounded px-2 py-1.5 text-sm" />
              </F>
            </div>
          </div>
          <div className={`text-xs rounded px-3 py-2 ${approved ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
            {approved
              ? `Authorised by ${c.approvedByName ?? 'unknown'} on ${String(c.approvedAt).slice(0, 10)} — the variances can now be posted.`
              : 'Not yet authorised. A stock adjustment must be approved by someone other than the person who raised it before it can be posted.'}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-xs"><tr>
                <th className="p-2 text-left" style={{ minWidth: 220 }}>Product</th><th className="text-left">Bin</th><th className="text-left">Batch</th>
                <th className="text-right">System</th><th className="text-right">Counted</th><th className="text-right">Variance</th><th className="text-left">Remarks</th>
              </tr></thead>
              <tbody>
                {items.map((i: any, idx: number) => {
                  const variance = i.countedQty === '' || i.countedQty == null ? null : Number(i.countedQty) - Number(i.systemQty);
                  return (
                    <tr key={i.id} className={`border-t ${variance ? 'bg-amber-50' : ''}`}>
                      <td className="p-2">{i.sku} — {i.name}</td>
                      <td className="font-mono text-xs">{i.bin || '—'}</td>
                      <td className="font-mono text-xs">{i.batchNo || '—'}</td>
                      <td className="text-right">{i.systemQty}</td>
                      <td className="text-right">
                        {editable
                          ? <input type="number" min={0} value={i.countedQty} onChange={(e) => setIt(idx, 'countedQty', e.target.value)} className="w-20 border rounded px-1 text-sm text-right" />
                          : (i.countedQty === '' || i.countedQty == null ? '—' : i.countedQty)}
                      </td>
                      <td className={`text-right ${variance == null ? '' : variance < 0 ? 'text-red-600' : variance > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
                        {variance == null ? '—' : `${variance > 0 ? '+' : ''}${variance}`}
                      </td>
                      <td>
                        {editable
                          ? <input value={i.remarks ?? ''} onChange={(e) => setIt(idx, 'remarks', e.target.value)} className="w-full border rounded px-1 text-sm" />
                          : <span className="text-xs text-slate-500">{i.remarks || '—'}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border px-4 py-1.5 rounded text-sm">Close</button>
          {editable && <button onClick={() => saveCounts.mutate()} disabled={saveCounts.isPending} className="border border-emerald-500 text-emerald-700 px-4 py-1.5 rounded text-sm disabled:opacity-50">{saveCounts.isPending ? 'Saving…' : 'Save Counts'}</button>}
          {editable && !approved && (
            <button onClick={() => approve.mutate()}
              disabled={approve.isPending || !allCounted || !reason || !remarks.trim()}
              title={!allCounted ? 'Count every line first' : (!reason || !remarks.trim()) ? 'A reason and remarks are required before approval' : undefined}
              className="border border-amber-500 text-amber-700 px-4 py-1.5 rounded text-sm disabled:opacity-50">
              {approve.isPending ? 'Approving…' : 'Approve Adjustment'}
            </button>
          )}
          {editable && (
            <button onClick={() => post.mutate()}
              disabled={post.isPending || !allCounted || !approved}
              title={!allCounted ? 'Count every line first' : !approved ? 'The adjustment must be approved before it can be posted' : undefined}
              className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
              {post.isPending ? 'Posting…' : 'Post Variances'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
